import React from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Package, Tag, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, thumbUrl } from "@/lib/api";
import type { Product, MediaAsset } from "@/lib/types";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { EmptyState } from "@/components/shared/EmptyState";
import { ProductScoreGauge } from "@/components/shared/ProductScoreGauge";
import { Button } from "@/components/ui/button";

const ProductCard: React.FC<{ product: Product; media?: MediaAsset }> = ({
  product,
  media,
}) => {
  const defaultThumb = media?.thumbnails.find((t) => t.is_default) ?? media?.thumbnails[0];
  return (
    <Link
      to={`/products/${product.product_id}`}
      data-testid={`product-card-${product.product_id}`}
      className="sg-card flex flex-col overflow-hidden group"
    >
      <div className="aspect-[16/10] bg-[color:var(--sg-muted-bg)] overflow-hidden relative">
        {defaultThumb ? (
          <img
            src={thumbUrl(media!.media_id, defaultThumb.thumb_id)}
            alt={product.title}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-[color:var(--sg-muted)]">
            <Package className="h-8 w-8" strokeWidth={1.5} />
          </div>
        )}
        {product.category && (
          <div className="absolute top-3 left-3">
            <span className="sg-chip bg-white/95 backdrop-blur border-[color:var(--sg-border)] text-[color:var(--sg-fg)]">
              {product.category}
            </span>
          </div>
        )}
        {product.source && product.source !== "manual" && (
          <div className="absolute top-3 right-3">
            <span className="sg-chip bg-white/95 backdrop-blur border-[color:var(--sg-border)] text-[color:var(--sg-fg)] text-[10px] uppercase tracking-wider">
              {product.source}
            </span>
          </div>
        )}
        <div className="absolute bottom-2 right-2">
          <ProductScoreGauge score={product.product_score ?? null} band={product.score_band || undefined} size="sm" />
        </div>
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <h3 className="font-display text-base font-semibold tracking-tight text-[color:var(--sg-fg)] line-clamp-1">
          {product.title}
        </h3>
        {product.description && (
          <p className="text-sm text-[color:var(--sg-muted)] mt-1 line-clamp-2">
            {product.description}
          </p>
        )}
        <div className="flex items-center justify-between mt-3">
          <div className="text-sm font-semibold text-[color:var(--sg-fg)]">
            {product.price ? `₹${product.price.toLocaleString()}` : "—"}
          </div>
          {product.tags?.length > 0 && (
            <div className="flex items-center gap-1 text-xs text-[color:var(--sg-muted)]">
              <Tag className="h-3 w-3" />
              {product.tags.length}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};

export const Products: React.FC = () => {
  const qc = useQueryClient();

  const products = useQuery<Product[]>({
    queryKey: ["products"],
    queryFn: async () => (await api.get("/products")).data,
  });
  const media = useQuery<MediaAsset[]>({
    queryKey: ["all-media"],
    queryFn: async () => (await api.get("/media")).data,
  });

  const mediaByProduct = new Map<string, MediaAsset>();
  for (const m of media.data || []) {
    if (m.product_id && !mediaByProduct.has(m.product_id)) {
      mediaByProduct.set(m.product_id, m);
    }
  }

  return (
    <DashboardLayout
      title="Products"
      subtitle="Every piece of content or product you promote lives here."
      seoPath="/products"
      seoDescription="All the products and content pieces you promote — organized with media, tags, categories and pricing."
      actions={
        <Link to="/products/new">
          <Button
            data-testid="new-product-btn"
            className="bg-[color:var(--sb-saffron)] hover:bg-[color:var(--sb-saffron-hover)] text-white"
          >
            <Plus className="h-4 w-4 mr-1.5" /> New product
          </Button>
        </Link>
      }
    >
      <div className="flex items-center justify-between mb-6">
        <div className="sg-label">
          {products.data?.length ?? 0} product{products.data?.length === 1 ? "" : "s"}
        </div>
      </div>

      {products.isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="sg-skeleton h-64" />
          ))}
        </div>
      ) : products.data && products.data.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="products-grid">
          {products.data.map((p) => (
            <ProductCard key={p.product_id} product={p} media={mediaByProduct.get(p.product_id)} />
          ))}
        </div>
      ) : (
        <EmptyState
          title="No products yet"
          description="Add your first product with the wizard — takes about 60 seconds."
          icon={<Package className="h-6 w-6 text-[color:var(--sg-muted)]" />}
          action={
            <Link to="/products/new">
              <Button className="bg-[color:var(--sb-saffron)] hover:bg-[color:var(--sb-saffron-hover)] text-white">
                <Plus className="h-4 w-4 mr-1.5" /> Add your first product
              </Button>
            </Link>
          }
        />
      )}
    </DashboardLayout>
  );
};
